import React from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ToggleRow from "../../../common/ToggleRow";
import {
  EMAIL_RESPONSE_POLICY_PRESETS,
  ensureSendEmailEnabledForPolicy,
} from "../emailPolicy";
import { ensureTaskQueuePlannerToolsPaired } from "../toolCatalog";

export default function EmailTab(props) {
  const {
    description,
    setEnabledTools,
    inboxAddress,
    allowAllSenders,
    setAllowAllSenders,
    acceptEmailAttachments,
    setAcceptEmailAttachments,
    senderWhitelist,
    setSenderWhitelist,
    newWhitelistEntry,
    setNewWhitelistEntry,
    emailResponsePolicyPreset,
    setEmailResponsePolicyPreset,
    emailResponsePolicyCustomPrompt,
    setEmailResponsePolicyCustomPrompt,
  } = props;

  const whitelistEntries = senderWhitelist
    .split("\n")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  const selectedEmailPreset =
    EMAIL_RESPONSE_POLICY_PRESETS.find(
      (preset) => preset.value === emailResponsePolicyPreset
    ) || EMAIL_RESPONSE_POLICY_PRESETS[0];
  const handleCopyInbox = () => {
    navigator.clipboard.writeText(inboxAddress);
  };
  const handleAddWhitelistEntry = () => {
    const entry = newWhitelistEntry.trim();
    if (!entry) return;
    const current = senderWhitelist.trim();
    setSenderWhitelist(current ? `${current}\n${entry}` : entry);
    setNewWhitelistEntry("");
  };
  const handleRemoveWhitelistEntry = (entryToRemove) => {
    const entries = senderWhitelist
      .split("\n")
      .map((s) => s.trim())
      .filter((s) => s.length > 0 && s !== entryToRemove);
    setSenderWhitelist(entries.join("\n"));
  };

  return (
    <>
      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: 600,
          color: "text.primary",
          mb: 0.5,
        }}
      >
        Email Inbox
      </Typography>
      <Typography sx={{ fontSize: "13px", color: "text.disabled", mb: 2 }}>
        This agent has a dedicated email inbox. Incoming emails that pass the
        whitelist will start or continue a chat session.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Typography className="org-field-label">Inbox Address</Typography>
        {inboxAddress ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              className="org-styled-textfield"
              fullWidth
              size="small"
              value={inboxAddress}
              InputProps={{ readOnly: true }}
            />
            <IconButton
              size="small"
              onClick={handleCopyInbox}
              title="Copy to clipboard"
            >
              <ContentCopyIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        ) : (
          <Typography
            sx={{
              fontSize: "13px",
              color: "text.disabled",
              fontStyle: "italic",
            }}
          >
            Inbox address will be assigned when the agent is created.
          </Typography>
        )}
      </Box>

      <Divider sx={{ borderColor: "divider", my: 2.5 }} />

      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: 600,
          color: "text.primary",
          mb: 0.5,
        }}
      >
        Sender Whitelist
      </Typography>
      <Typography sx={{ fontSize: "13px", color: "text.disabled", mb: 2 }}>
        Control who can email this agent. Add email addresses or domain patterns
        (e.g. @company.com).
      </Typography>

      <Box sx={{ mb: 2 }}>
        <ToggleRow
          checked={allowAllSenders}
          onChange={setAllowAllSenders}
          title="Allow all senders"
          description="When disabled, only addresses in the whitelist below can email this agent."
        />
      </Box>

      {!allowAllSenders && (
        <Box>
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <TextField
              className="org-styled-textfield"
              fullWidth
              size="small"
              value={newWhitelistEntry}
              onChange={(e) => setNewWhitelistEntry(e.target.value)}
              placeholder="user@example.com or @example.com"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddWhitelistEntry();
                }
              }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={handleAddWhitelistEntry}
              disabled={!newWhitelistEntry.trim()}
              sx={{ minWidth: 60, textTransform: "none", fontSize: "13px" }}
            >
              Add
            </Button>
          </Box>

          {whitelistEntries.length > 0 ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {whitelistEntries.map((entry, idx) => (
                <Chip
                  key={idx}
                  label={entry}
                  size="small"
                  onDelete={() => handleRemoveWhitelistEntry(entry)}
                  sx={{ fontSize: "12px" }}
                />
              ))}
            </Box>
          ) : (
            <Typography
              sx={{
                fontSize: "13px",
                color: "text.disabled",
                fontStyle: "italic",
              }}
            >
              No senders whitelisted. The agent will not process any incoming
              emails.
            </Typography>
          )}
        </Box>
      )}

      <Divider sx={{ borderColor: "divider", my: 2.5 }} />

      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: 600,
          color: "text.primary",
          mb: 0.5,
        }}
      >
        Email Response Rules
      </Typography>
      <Typography sx={{ fontSize: "13px", color: "text.disabled", mb: 2 }}>
        Controls how the AI should handle outbound responses when the user
        message comes from email.
      </Typography>

      <Typography className="org-field-label">Preset</Typography>
      <TextField
        className="org-styled-textfield"
        select
        fullWidth
        size="small"
        value={emailResponsePolicyPreset}
        onChange={(e) => {
          const nextPreset = e.target.value;
          setEmailResponsePolicyPreset(nextPreset);
          setEnabledTools((prev) =>
            ensureTaskQueuePlannerToolsPaired(
              ensureSendEmailEnabledForPolicy(prev, nextPreset)
            )
          );
        }}
        sx={{ mb: 0.75 }}
      >
        {EMAIL_RESPONSE_POLICY_PRESETS.map((preset) => (
          <MenuItem key={preset.value} value={preset.value}>
            {preset.label}
          </MenuItem>
        ))}
      </TextField>
      <Typography sx={{ fontSize: "12px", color: "text.disabled", mb: 2 }}>
        {selectedEmailPreset?.description}
      </Typography>

      <Typography className="org-field-label">Custom Instructions</Typography>
      <TextField
        className="org-styled-textfield"
        fullWidth
        multiline
        minRows={4}
        value={emailResponsePolicyCustomPrompt}
        onChange={(e) => setEmailResponsePolicyCustomPrompt(e.target.value)}
        placeholder="Optional instructions for email handling, tone, or escalation steps."
      />
      <Typography sx={{ fontSize: "11px", color: "text.disabled", mt: 0.75 }}>
        This text is injected into the system prompt only when handling inbound
        email user messages.
      </Typography>

      <Divider sx={{ borderColor: "divider", my: 2.5 }} />

      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: 600,
          color: "text.primary",
          mb: 0.5,
        }}
      >
        Email Attachments
      </Typography>
      <Typography sx={{ fontSize: "13px", color: "text.disabled", mb: 2 }}>
        When enabled, file attachments on incoming emails will be uploaded to
        this agent's file library and made available to the agent.
      </Typography>

      <Box sx={{ mb: 2 }}>
        <ToggleRow
          checked={acceptEmailAttachments}
          onChange={setAcceptEmailAttachments}
          title="Accept email attachments"
          description="Upload inbound email attachments to the agent's file library so it can read, edit, and delete them."
        />
      </Box>
    </>
  );
}
